// Minimal Markdown -> HTML renderer shared by Strategy and the agency
// analysis screens. Headings, lists, paragraphs, bold/italic/code, links,
// horizontal rules and simple pipe tables. Input is escaped first.

export function renderMd(src) {
  // The quote characters matter as much as the angle brackets: every
  // interpolation below lands inside a double-quoted HTML attribute at least
  // once (the link href), and without escaping them a value can close the
  // attribute and open an event handler.
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Belt and braces over the escape above: a URL that still carries a quote
    // or an entity is not a URL we are willing to put in an attribute, so it
    // renders as plain text instead of a link.
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
      (m, text, url) => (/["'`&]/.test(url) ? m : `<a href="${url}" target="_blank" rel="noreferrer">${text}</a>`));
  const lines = (src || '').split('\n');
  const out = [];
  let listType = null;
  let table = null;
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };
  const closeTable = () => {
    if (!table) return;
    const [head, ...rows] = table.filter((r) => !/^\s*\|?\s*:?-{2,}/.test(r));
    const cells = (r) => r.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => inline(c.trim()));
    out.push('<table><thead><tr>' + cells(head).map((c) => `<th>${c}</th>`).join('') + '</tr></thead><tbody>'
      + rows.map((r) => '<tr>' + cells(r).map((c) => `<td>${c}</td>`).join('') + '</tr>').join('')
      + '</tbody></table>');
    table = null;
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (/^\s*\|.*\|\s*$/.test(line)) { closeList(); (table ||= []).push(line); continue; }
    closeTable();
    if (/^# /.test(line))   { closeList(); out.push(`<h1>${inline(line.slice(2))}</h1>`); continue; }
    if (/^## /.test(line))  { closeList(); out.push(`<h2>${inline(line.slice(3))}</h2>`); continue; }
    if (/^### /.test(line)) { closeList(); out.push(`<h3>${inline(line.slice(4))}</h3>`); continue; }
    if (/^\s*---\s*$/.test(line)) { closeList(); out.push('<hr>'); continue; }
    const olM = /^\s*(\d+)\.\s+(.*)$/.exec(line);
    if (olM) {
      if (listType !== 'ol') { closeList(); out.push('<ol>'); listType = 'ol'; }
      out.push(`<li>${inline(olM[2])}</li>`);
      continue;
    }
    const ulM = /^\s*[-*]\s+(.*)$/.exec(line);
    if (ulM) {
      if (listType !== 'ul') { closeList(); out.push('<ul>'); listType = 'ul'; }
      out.push(`<li>${inline(ulM[1])}</li>`);
      continue;
    }
    if (line.trim() === '') { closeList(); out.push(''); continue; }
    closeList();
    if (/^_.+_$/.test(line.trim())) out.push(`<p class="lede">${inline(line.trim().slice(1, -1))}</p>`);
    else out.push(`<p>${inline(line)}</p>`);
  }
  closeTable();
  closeList();
  return out.join('\n');
}

// Fetch a text object from the private post-packages bucket via a signed URL.
export async function fetchPackageText(supabase, path, ttl = 300) {
  const { data: sig, error } = await supabase.storage.from('post-packages').createSignedUrl(path, ttl);
  if (error) throw error;
  const resp = await fetch(sig.signedUrl);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.text();
}
